import {
	AvatarUploadField,
	Button,
	FormikWithRedirectForm,
	Id,
	ImageResource,
	ImageUploadFieldValues,
	Loading,
	Row,
	SubmitButton,
	Trigger,
	UserService,
	isSuccess,
	useToasts,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FormikHelpers } from "formik";
import { FC, Fragment, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { AnyProfileHandler } from "../../../shared/contacts/profile/profileContactAttributeMapper";
import { profileService, useProfile } from "../../../shared/profile/profileService";
import { ChangePasswordDrawer, PasswordFormValues } from "./ChangePasswordDrawer";

interface BaseProfileProps {
	afterSuccessfulSave?: () => void;
	userService: UserService;
}

interface FormValues {
	attributes: Record<string, AnyProfileHandler>;
	image: ImageResource | null;
	imageUpload: ImageUploadFieldValues | null;
}

const AvatarContainer = styled.div`
	margin: auto;
	width: 300px;
`;

export const BaseProfileEdit: FC<BaseProfileProps> = ({ afterSuccessfulSave, userService }) => {
	const { showToast } = useToasts();
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { profile } = useProfile();

	const uploadUserImage = useCallback(
		async (imageUpload: ImageUploadFieldValues) => {
			const croppedImageResponse = await userService.saveImage(imageUpload);

			if (isSuccess(croppedImageResponse)) {
				return croppedImageResponse.data.data;
			}
		},
		[userService],
	);

	const updateUser = useCallback(
		async ({ image, imageUpload, attributes }: FormValues, helpers: FormikHelpers<FormValues>) => {
			const croppedImage = (imageUpload?.file && (await uploadUserImage(imageUpload))) || image;

			const body = Object.values(attributes)
				.filter((h) => h.type !== "PRIMARY_EMAIL")
				.map((h) => h.toRequestValue());

			const response = await profileService.update({ attributes: body, imageId: croppedImage?.id });

			if (isSuccess(response)) {
				void queryClient.invalidateQueries({ queryKey: userService.keys.me });
				helpers.resetForm({ values: { attributes, image, imageUpload } });
				afterSuccessfulSave && afterSuccessfulSave();
				showToast({ type: "success", title: t("Profile"), body: t("Profile was saved.") });

				return true;
			}

			return false;
		},
		[afterSuccessfulSave, queryClient, showToast, t, uploadUserImage, userService],
	);

	const submitPasswordChange = useCallback(
		async (values: PasswordFormValues) => {
			const response = await userService.updatePassword(values);
			if (isSuccess(response)) {
				showToast({
					body: t("Password was successfully changed."),
					title: t("Password"),
					type: "success",
				});
			}
		},
		[showToast, t, userService],
	);

	const initialValues = useMemo<FormValues | null>(() => {
		if (profile === undefined) return null;
		const attributes = profile.attributes
			.filter((h) => h !== null)
			.reduce(
				(values, handler: AnyProfileHandler) => ({ ...values, [handler.id]: handler }),
				{} as Record<string, AnyProfileHandler>,
			);

		return {
			image: profile.image,
			imageUpload: null,
			attributes,
		};
	}, [profile]);

	const validate = (formValues: FormValues) => {
		if (profile === undefined) return {};
		type AttributeErrors = Record<Id, { value: Record<string, string> | string }>;
		const attributeErrors = profile.attributes
			.filter((h) => h !== null)
			.reduce((previousErrors, handler) => {
				const handlerErrors = handler.validate(t, formValues.attributes[handler.id].value);
				if (handlerErrors && (typeof handlerErrors === "string" || Object.keys(handlerErrors).length > 0)) {
					return { ...previousErrors, [handler.id]: { value: handlerErrors } };
				} else {
					return previousErrors;
				}
			}, {} as AttributeErrors);

		if (Object.keys(attributeErrors).length > 0) {
			return { attributes: attributeErrors };
		}
	};

	if (!profile || !initialValues) return <Loading />;

	return (
		<FormikWithRedirectForm onSubmit={updateUser} initialValues={initialValues} validate={validate} enableReinitialize>
			<AvatarContainer>
				<AvatarUploadField maxHeight={300} ariaLabel={t("Upload avatar")} name="imageUpload" imageName="image" />
			</AvatarContainer>
			{profile.attributes
				.filter((handler) => handler !== null)
				.map((handler) => (
					<Fragment key={handler.id}>{handler.renderFormField(t, "attributes")}</Fragment>
				))}
			<Row $justifyContent="space-between">
				<Trigger
					render={(renderProps) => (
						<ChangePasswordDrawer
							open={renderProps.open}
							onClose={renderProps.handleClose}
							onChangePassword={submitPasswordChange}
						/>
					)}
				>
					{(triggerProps) => (
						<Button type="button" {...triggerProps}>
							{t("Change password")}
						</Button>
					)}
				</Trigger>
				<SubmitButton>{t("Save")}</SubmitButton>
			</Row>
		</FormikWithRedirectForm>
	);
};
